<template>
    <div class="reproductor" :class="{'reproductor--youtube': esYoutube}">
        <video
            v-if="!esYoutube && videoUrl"
            ref="video"
            :key="videoUrl"
            :src="videoUrl"
            :controls="controles"
            :muted="silenciado"
            playsinline
            preload="auto"
            disablepictureinpicture
            controlslist="nodownload noplaybackrate noremoteplayback"
            class="reproductor__video"
            @play="alReproducir"
            @ended="alTerminar"
            @error="alFallar"
            @volumechange="alCambiarVolumen"
            @contextmenu.prevent
        ></video>
        <div v-else-if="esYoutube" ref="marco" class="reproductor__marco"></div>
    </div>
</template>

<script>
/**
 * Reproductor común para los videos del sistema: un MP4 del almacenamiento
 * privado (enlace firmado) o un video de YouTube por su id. Avisa cuando
 * empieza ("inicio", una sola vez) y cuando termina ("fin"), y se puede pausar,
 * reanudar y silenciar desde fuera, con la misma interfaz para ambas fuentes.
 *
 *   <reproductor-video :video-url="url" :youtube-id="id" autoplay silenciado @inicio @fin/>
 */
let cargaYouTube = null;

function cargarYouTube() {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);

    if (!cargaYouTube) {
        cargaYouTube = new Promise((resolve, reject) => {
            const previo = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (typeof previo === "function") previo();
                resolve(window.YT);
            };
            const script = document.createElement("script");
            script.src = "https://www.youtube.com/iframe_api";
            script.async = true;
            script.onerror = () => {
                cargaYouTube = null;
                reject(new Error("No se pudo cargar YouTube"));
            };
            document.head.appendChild(script);
        });
    }

    return cargaYouTube;
}

export default {
    name: "ReproductorVideo",
    props: {
        videoUrl: {type: String, default: null},
        youtubeId: {type: String, default: null},
        autoplay: {type: Boolean, default: true},
        // Arranca sin sonido (los navegadores solo permiten el autoplay silenciado).
        silenciado: {type: Boolean, default: false},
        controles: {type: Boolean, default: true},
    },
    data() {
        return {
            iniciado: false,
            player: null,
            destruido: false,
        };
    },
    computed: {
        esYoutube() {
            return !!this.youtubeId && !this.videoUrl;
        },
    },
    watch: {
        youtubeId() {
            this.reiniciarYoutube();
        },
        videoUrl() {
            this.iniciado = false;
            if (this.autoplay) this.$nextTick(() => this.reproducir());
        },
    },
    methods: {
        /** Intenta reproducir; si el navegador bloquea el sonido, sigue en silencio. */
        reproducir() {
            if (this.esYoutube) {
                if (this.player && this.player.playVideo) this.player.playVideo();
                return;
            }

            const video = this.$refs.video;
            if (!video) return;

            const intento = video.play();
            if (intento && intento.catch) {
                intento.catch(() => {
                    video.muted = true;
                    this.$emit("silencio", true);
                    video.play().catch(() => this.$emit("error"));
                });
            }
        },
        pausar() {
            if (this.esYoutube) {
                if (this.player && this.player.pauseVideo) this.player.pauseVideo();
            } else if (this.$refs.video && !this.$refs.video.paused) {
                this.$refs.video.pause();
            }
        },
        reanudar() {
            if (!this.iniciado) return;
            this.reproducir();
        },
        silenciar(valor) {
            if (this.esYoutube) {
                if (!this.player) return;
                valor ? this.player.mute() : this.player.unMute();
                this.$emit("silencio", !!valor);
            } else if (this.$refs.video) {
                this.$refs.video.muted = !!valor;
            }
        },
        estaSilenciado() {
            if (this.esYoutube) return this.player && this.player.isMuted ? this.player.isMuted() : this.silenciado;
            return this.$refs.video ? this.$refs.video.muted : this.silenciado;
        },
        alReproducir() {
            if (this.iniciado) return;
            this.iniciado = true;
            this.$emit("inicio");
        },
        alTerminar() {
            this.$emit("fin");
        },
        alFallar() {
            this.$emit("error");
        },
        alCambiarVolumen() {
            if (this.$refs.video) this.$emit("silencio", this.$refs.video.muted);
        },
        montarYoutube() {
            if (!this.esYoutube || !this.$refs.marco) return;

            cargarYouTube().then(YT => {
                if (this.destruido || !this.$refs.marco) return;

                // YouTube reemplaza el elemento por su iframe: se le da uno propio cada vez.
                const destino = document.createElement("div");
                this.$refs.marco.innerHTML = "";
                this.$refs.marco.appendChild(destino);

                this.player = new YT.Player(destino, {
                    host: "https://www.youtube-nocookie.com",
                    videoId: this.youtubeId,
                    playerVars: {
                        autoplay: this.autoplay ? 1 : 0,
                        mute: this.silenciado ? 1 : 0,
                        controls: this.controles ? 1 : 0,
                        rel: 0,
                        playsinline: 1,
                        modestbranding: 1,
                        origin: window.location.origin,
                    },
                    events: {
                        onStateChange: (evento) => {
                            if (evento.data === YT.PlayerState.PLAYING) this.alReproducir();
                            if (evento.data === YT.PlayerState.ENDED) this.alTerminar();
                        },
                        onError: () => this.alFallar(),
                    },
                });
            }).catch(() => this.alFallar());
        },
        desmontarYoutube() {
            if (this.player && this.player.destroy) {
                try {
                    this.player.destroy();
                } catch (e) { /* el reproductor ya no existe */ }
            }
            this.player = null;
            if (this.$refs.marco) this.$refs.marco.innerHTML = "";
        },
        reiniciarYoutube() {
            this.desmontarYoutube();
            this.iniciado = false;
            this.$nextTick(() => this.montarYoutube());
        },
    },
    mounted() {
        if (this.esYoutube) {
            this.montarYoutube();
        } else if (this.autoplay) {
            this.reproducir();
        }
    },
    beforeDestroy() {
        this.destruido = true;
        this.desmontarYoutube();
    },
}
</script>

<style lang="scss" scoped>
.reproductor {
    background: #000;
    width: 100%;
}

.reproductor__video {
    display: block;
    width: 100%;
    max-height: 70vh;
    background: #000;
}

.reproductor__marco {
    position: relative;
    width: 100%;
    padding-top: 56.25%; // 16:9

    ::v-deep iframe {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
    }
}
</style>
